import { useMemo } from 'react'
import { useIntl } from 'react-intl'

import styled from 'styled-components'
import { rem } from 'polished'

import useMerchandiseBrandsList from 'hooks/Merchandise/useMerchandiseBrandsList'
import useMerchandiseBrands from 'hooks/Merchandise/useMerchandiseBrands'

import Sheet from 'components/utils/Sheet'
import Group from 'components/utils/Group'
import { mediaQueryUp } from 'components/utils/breakpoint'
import ScrollAnchor from 'components/Common/ScrollAnchor'
import LayoutContainer from 'components/Common/LayoutContainer'
import Clickable from 'components/Common/Clickable'

import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'

import MerchandiseBrandsHeader from './MerchandiseBrandsHeader'
import MerchandiseBrandsSkeleton from './MerchandiseBrandsSkeleton'
import { getMerchandiseBrandSearchUrl } from '../merchandiseUtils'

const GridContainer = styled.div`
  display: grid;
  gap: ${rem(20)};
  grid-template-columns: repeat(2, 1fr);
  
  ${mediaQueryUp.tablet} {
    grid-template-columns: repeat(4, 1fr)
  }
`

const ALPHABET_LIST = ['#', 'A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z']

function MerchandiseBrandsPage() {
  const intl = useIntl()
  const [brandListIds, fetchingList] = useMerchandiseBrandsList({ sortBy: 'top' })
  const [brands, fetchingTopBrands] = useMerchandiseBrands(brandListIds)

  const brandsByLetter = useMemo(() => {
    return ALPHABET_LIST.map(letter => {
      if (letter === '#') {
        // symbols and numbers
        const brandsMatchingSymbol = brands.filter(brand => /^[^a-z]/i.test(brand.name))
        return {
          value: letter,
          brands: brandsMatchingSymbol,
          isDisabled: brandsMatchingSymbol.length === 0,
        }
      }

      const brandsMatchingLetter = brands
        .filter(brand => brand.name.toUpperCase().startsWith(letter))

      return {
        value: letter,
        brands: brandsMatchingLetter,
        isDisabled: brandsMatchingLetter.length === 0,
      }
    })
  }, [brands])

  const alphabetWithBrands = useMemo(() => {
    return brandsByLetter.filter(letter => letter.brands.length > 0)
  }, [brandsByLetter])

  return <Sheet padding="40 0">
    <Group direction="vertical" gap={20}>
      <LayoutContainer>
        <Heading variant="heading1">
          {intl.formatMessage({
            id: 'G_6J26rbrd4R5Yux2Jrc',
            defaultMessage: 'Brand directory',
            description: 'Page heading for the merchandise brands directory page',
          })}
        </Heading>
      </LayoutContainer>

      <MerchandiseBrandsHeader brandsByLetter={brandsByLetter} />

      <LayoutContainer>
        <Group direction="vertical" gap={40}>
          {(fetchingTopBrands || fetchingList) && <>
            <MerchandiseBrandsSkeleton />
            <MerchandiseBrandsSkeleton />
            <MerchandiseBrandsSkeleton />
            <MerchandiseBrandsSkeleton />
          </>}

          {alphabetWithBrands.map(letter => (
            <Group direction="vertical" gap={12} key={letter.value}>
              <ScrollAnchor id={letter.value} vOffset={rem(50)} />
              <Heading variant="heading4">{letter.value}</Heading>

              <GridContainer>
                {letter.brands.map(brand => (
                  <Clickable key={brand.id} to={getMerchandiseBrandSearchUrl(brand)}>
                    <BodyText variant="medium">{brand.name}</BodyText>
                  </Clickable>
                ))}
              </GridContainer>
            </Group>
          ))}
        </Group>
      </LayoutContainer>
    </Group>
  </Sheet>
}

export default MerchandiseBrandsPage
